import React from "react";
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
  ColumnDef,
  getFilteredRowModel,
  getSortedRowModel,
  SortDirection
} from "@tanstack/react-table";
import {
  Box,
  Table as TableBase,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from "@mui/material";
import { TableProps, WithSortProps } from "./Table.types";
import { useTranslation } from "react-i18next";
import Pagination from "../Pagination";

function WithSort<TType, TValue>(props: WithSortProps<TType, TValue>) {
  const getSortDirection = (
    isSorted: SortDirection | false
  ): SortDirection | undefined => {
    if (isSorted !== false) {
      return isSorted;
    }
  };

  if (props.header.column.getCanSort()) {
    return (
      <Typography variant="subtitle1" fontWeight="medium">
        <TableSortLabel
          direction={getSortDirection(props.header.column.getIsSorted())}
          active={props.header.column.getIsSorted() !== false}
          onClick={props.header.column.getToggleSortingHandler()}
        >
          {props.children}
        </TableSortLabel>
      </Typography>
    );
  }
  return (
    <Typography component="span" variant="subtitle1" fontWeight="medium">
      {props.children}
    </Typography>
  );
}

function CellContent({ cell, children }) {
  const { cellType } = cell.column.columnDef.meta ?? {};

  if (cellType === "custom") {
    return <>{children}</>;
  }

  return (
    <Typography
      component="span"
      variant="body1"
      title={
        cell.column.columnDef.meta?.canOverflow && (cell.getValue() as string)
      }
    >
      {children}
    </Typography>
  );
}

function Table<TType>(props: TableProps<TType>) {
  const { t } = useTranslation();
  const data = React.useMemo<TType[]>(() => props.data, [props.data]);
  const columns = React.useMemo<ColumnDef<TType, any>[]>(
    () => props.columns,
    [props.columns]
  );

  const table = useReactTable<TType>({
    data: data,
    columns: columns,
    state: { ...props.tableState },
    autoResetPageIndex: false,
    defaultColumn: {
      minSize: 0
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    ...props.tableOptions
  });

  const goToPage = React.useCallback(
    (_event: React.ChangeEvent<unknown>, page: number): void => {
      table.setPageIndex(page - 1);
    },
    [table]
  );

  return (
    <TableContainer>
      <TableBase sx={{ tableLayout: "fixed" }}>
        <TableHead>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableCell key={header.id} width={`${header.getSize()}%`}>
                  <WithSort header={header}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </WithSort>
                  {header.column.columnDef?.meta?.filterComponent
                    ? header.column.columnDef?.meta?.filterComponent({
                        column: header.column,
                        table: table
                      })
                    : null}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell height={100} colSpan={columns.length}>
                <Typography textAlign="center">
                  {t("common.noRecordsFound")}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} width={`${cell.column.getSize()}%`}>
                  <CellContent cell={cell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </CellContent>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableBase>
      <Box display="flex" justifyContent="center" marginTop={2}>
        <Pagination
          sx={{ visibility: table.getPageCount() ? "visible" : "hidden" }}
          count={table.getPageCount()}
          page={table.getState().pagination.pageIndex + 1}
          onChange={goToPage}
          {...props.pagination}
        />
      </Box>
    </TableContainer>
  );
}

export default Table;
